/**
 * NotificationChannelForm — "Add Notification Channel".
 *
 * Registers a notification channel (Slack / Microsoft Teams / Webhook / Email) that findings and
 * governance events can be routed to. `POST` via `useCreateNotificationChannel()`.
 *
 * Pattern: `react-hook-form` + `zodResolver`, schema imported from `NotificationChannel.schema.ts`
 * (never redefined — the channel-type-conditional required fields live in its `superRefine`),
 * Fluent `Field` for verbatim validation messages, server errors mapped via the shared
 * `applyServerErrors` (Charter M3 / form-standard.context.md). The outer `Dialog` chrome is owned
 * by `IntegrationsPage.tsx` (this feature's own `components/`, not `src/pages/**`) — this
 * component renders the form CONTENT only and keeps its existing `onSuccess`/`onCancel` prop
 * contract so the hosting page needs no change.
 */
import type { FC } from 'react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Field, Input, Select, makeStyles, tokens } from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
import { useCreateNotificationChannel } from '../hooks';
import type { CreateNotificationChannelRequest } from '../types';
import { applyServerErrors, useReportFormDirty, FormActions, ServerErrorBanner } from '../../../components/forms';
import { NOTIFICATION_CHANNEL_TYPE_OPTIONS } from './connectionFormFields';
import {
  NotificationChannelSchema,
  type NotificationChannelFormValues,
  type NotificationChannelFormOutput,
} from './NotificationChannel.schema';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  typeSpecificSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    paddingTop: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalS,
  },
});

// Known request-DTO field keys — used by the server-error -> field mapping.
const KNOWN_FIELDS = [
  'name',
  'channelType',
  'webhookUrl',
  'webhookSecret',
  'slackChannelId',
  'slackChannelName',
  'teamsTeamId',
  'teamsChannelId',
  'teamsChannelName',
] as const;

const DEFAULT_VALUES: NotificationChannelFormValues = {
  name: '',
  channelType: 'Slack',
  webhookUrl: '',
  webhookSecret: '',
  slackChannelId: '',
  slackChannelName: '',
  teamsTeamId: '',
  teamsChannelId: '',
  teamsChannelName: '',
};

export interface NotificationChannelFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Reports dirty state up so the hosting dialog can guard an unsaved-changes dismiss. */
  onDirtyChange?: (dirty: boolean) => void;
}

export const NotificationChannelForm: FC<NotificationChannelFormProps> = ({
  onSuccess,
  onCancel,
  onDirtyChange,
}) => {
  const styles = useStyles();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<NotificationChannelFormValues, unknown, NotificationChannelFormOutput>({
    resolver: zodResolver(NotificationChannelSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES,
  });

  useReportFormDirty(isDirty, onDirtyChange);

  const channelType = watch('channelType');

  const [globalError, setGlobalError] = useState<string | undefined>();

  const createChannel = useCreateNotificationChannel();

  const onValid = (values: NotificationChannelFormOutput) => {
    // Guard double-submit.
    if (createChannel.isPending) return;
    setGlobalError(undefined);

    const request: CreateNotificationChannelRequest = {
      name: values.name,
      channelType: values.channelType,
      webhookUrl: values.webhookUrl,
      webhookSecret: values.webhookSecret,
      slackChannelId: values.slackChannelId,
      slackChannelName: values.slackChannelName,
      teamsTeamId: values.teamsTeamId,
      teamsChannelId: values.teamsChannelId,
      teamsChannelName: values.teamsChannelName,
    };

    createChannel.mutate(request, {
      onSuccess: () => {
        reset(DEFAULT_VALUES);
        onSuccess?.();
      },
      onError: (error) => {
        const { globalMessage } = applyServerErrors(error, KNOWN_FIELDS, setError);
        setGlobalError(globalMessage);
      },
    });
  };

  const renderTypeSpecificFields = () => {
    switch (channelType) {
      case 'Slack':
        return (
          <>
            <Field
              label="Slack Channel Name"
              required
              validationState={errors.slackChannelName ? 'error' : undefined}
              validationMessage={errors.slackChannelName?.message}
              hint="The name of the Slack channel (e.g., #security-alerts)"
            >
              <Controller
                name="slackChannelName"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    data-testid="notification-channel-form-slackChannelName"
                    value={field.value ?? ''}
                    placeholder="#security-alerts"
                    disabled={createChannel.isPending}
                  />
                )}
              />
            </Field>
            <Field label="Slack Channel ID" hint="Optional. The ID of the Slack channel (e.g., C01234567)">
              <Controller
                name="slackChannelId"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    data-testid="notification-channel-form-slackChannelId"
                    value={field.value ?? ''}
                    placeholder="C01234567"
                    disabled={createChannel.isPending}
                  />
                )}
              />
            </Field>
          </>
        );
      case 'MicrosoftTeams':
        return (
          <>
            <Field
              label="Teams Channel Name"
              required
              validationState={errors.teamsChannelName ? 'error' : undefined}
              validationMessage={errors.teamsChannelName?.message}
              hint="The name of the Teams channel"
            >
              <Controller
                name="teamsChannelName"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    data-testid="notification-channel-form-teamsChannelName"
                    value={field.value ?? ''}
                    placeholder="Security Alerts"
                    disabled={createChannel.isPending}
                  />
                )}
              />
            </Field>
            <Field label="Teams Team ID" hint="Optional. The ID of the Teams team">
              <Controller
                name="teamsTeamId"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    data-testid="notification-channel-form-teamsTeamId"
                    value={field.value ?? ''}
                    placeholder="Team ID"
                    disabled={createChannel.isPending}
                  />
                )}
              />
            </Field>
            <Field label="Teams Channel ID" hint="Optional. The ID of the Teams channel">
              <Controller
                name="teamsChannelId"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    data-testid="notification-channel-form-teamsChannelId"
                    value={field.value ?? ''}
                    placeholder="Channel ID"
                    disabled={createChannel.isPending}
                  />
                )}
              />
            </Field>
          </>
        );
      case 'Webhook':
        return (
          <Field label="Webhook Secret" hint="Optional. A secret for signing webhook payloads">
            <Controller
              name="webhookSecret"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="password"
                  data-testid="notification-channel-form-webhookSecret"
                  value={field.value ?? ''}
                  placeholder="Secret for HMAC signature"
                  disabled={createChannel.isPending}
                />
              )}
            />
          </Field>
        );
      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit(onValid)} className={styles.form} noValidate>
      <ServerErrorBanner title="Error" message={globalError} testId="notification-channel-form-server-error" />

      <Field
        label="Channel Name"
        required
        validationState={errors.name ? 'error' : undefined}
        validationMessage={errors.name?.message}
      >
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="notification-channel-form-name"
              value={field.value ?? ''}
              placeholder="Security Alerts - Slack"
              disabled={createChannel.isPending}
            />
          )}
        />
      </Field>

      <Field label="Channel Type" required>
        <Controller
          name="channelType"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value}
              onChange={(_, data) => field.onChange(data.value)}
              onBlur={field.onBlur}
              disabled={createChannel.isPending}
              data-testid="notification-channel-form-channelType"
            >
              {NOTIFICATION_CHANNEL_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
        />
      </Field>

      <Field
        label="Webhook URL"
        required
        validationState={errors.webhookUrl ? 'error' : undefined}
        validationMessage={errors.webhookUrl?.message}
        hint={
          channelType === 'Slack'
            ? 'Slack Incoming Webhook URL'
            : channelType === 'MicrosoftTeams'
            ? 'Teams Incoming Webhook URL'
            : 'The URL to send notifications to'
        }
      >
        <Controller
          name="webhookUrl"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="notification-channel-form-webhookUrl"
              value={field.value ?? ''}
              placeholder={
                channelType === 'Slack'
                  ? 'https://hooks.slack.com/services/...'
                  : channelType === 'MicrosoftTeams'
                  ? 'https://outlook.office.com/webhook/...'
                  : 'https://api.example.com/webhook'
              }
              disabled={createChannel.isPending}
            />
          )}
        />
      </Field>

      <div className={styles.typeSpecificSection}>{renderTypeSpecificFields()}</div>

      <FormActions
        mode="create"
        testIdPrefix="notification-channel-form"
        isSubmitting={createChannel.isPending}
        onCancel={onCancel}
        submitLabel="Create Channel"
        submittingLabel="Creating…"
        submitIcon={<AddRegular />}
      />
    </form>
  );
};
